import {
  Button,
  ButtonProps,
  ListItemIcon,
  Menu as MuiMenu,
  MenuProps as MuiMenuProps,
  MenuItem,
  TypographyProps,
  SxProps,
} from '@mui/material';
import React, { ReactElement, useState } from 'react';
import { Icons, Text, Theme } from '@/theme/index';

interface MenuItem {
  icon?: JSX.Element;
  display: string;
  action: () => void;
  count?: JSX.Element | null;
  disabled?: boolean;
  hidden?: boolean;
}

export type MuiMenuPropsWithoutOpen = Omit<MuiMenuProps, 'open'>;

interface MenuProps extends ButtonProps {
  buttonText?: string;
  showIcon?: boolean;
  customIcon?: ReactElement;
  textProps?: TypographyProps;
  menuItems: MenuItem[];
  iconSize?: 'small' | 'large';
  count?: ReactElement;
  muiMenuProps?: MuiMenuPropsWithoutOpen;
  menuItemStyles?: SxProps<Theme>;
  handleTrackEvent?: () => void;
}

/**
 * A menu displays a list of choices on a temporary surface. It appears when the user interacts with a button
 */
export const CustomMenu = React.forwardRef<HTMLButtonElement, MenuProps>(
  (props, ref) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (
      event: React.MouseEvent<HTMLButtonElement>,
      handleTrackEvent: (() => void) | null
    ) => {
      if (handleTrackEvent) handleTrackEvent();
      setAnchorEl(event.currentTarget);
    };
    const handleClose = (clickedItem?: MenuItem) => {
      clickedItem?.action();
      setAnchorEl(null);
    };
    const {
      showIcon,
      menuItems,
      buttonText,
      customIcon,
      color,
      iconSize,
      textProps,
      muiMenuProps = { MenuListProps: {} },
      menuItemStyles,
      handleTrackEvent,
      ...rest
    } = props;
    const renderIcon = customIcon || <Icons.MoreHoriz fontSize={iconSize} />;
    return (
      <>
        <Button
          ref={ref}
          data-testid="menu-button"
          aria-controls={open ? 'menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={(event) => handleClick(event, handleTrackEvent || null)}
          color={color}
          {...rest}
        >
          {showIcon && renderIcon} {buttonText}
        </Button>
        <MuiMenu
          {...muiMenuProps}
          anchorEl={anchorEl}
          open={open}
          onClose={() => handleClose()}
          MenuListProps={{
            'aria-labelledby': 'menu',
            ...muiMenuProps.MenuListProps,
          }}
        >
          {menuItems
            .filter((item) => !item.hidden)
            .map((item, index) => (
              <MenuItem
                sx={{ minWidth: '185px', ...menuItemStyles }}
                key={`${item.display}-${index}`}
                onClick={() => handleClose(item)}
                disabled={item.disabled}
              >
                {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
                <Text {...textProps}>{item.display}</Text>{' '}
                {item.count && item.count}
              </MenuItem>
            ))}
        </MuiMenu>
      </>
    );
  }
);
